/* eslint-disable */
import React, { useState } from 'react';
import { Collapse, Select, type CollapseProps } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import styles from '@/components/Dictionary/Dictionary.module.less';
import { useAuth } from '../../hooks/useAuth';
// Temporarily comment out the import until the component is created
// import CreateWordModal from '../Wordbook/CreateWordModal';

interface DictionaryEntry {
  source: string;
  content: string;
  type: 'html' | 'text';
  isEmpty: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  onRequest?: () => void;
  onRefresh?: () => void;
  requested?: boolean;
  sourceType?: string;
  refreshCooldown?: number;
}

interface RelatedWord {
  id: number;
  w: string;
  m: string;
  similarity?: number;
}

interface FuzzyMatch {
  id: number;
  w: string;
  m: string;
  matchType: 'prefix' | 'contains' | 'phonetic' | 'tag';
}

interface DictionaryResultProps {
  entries: DictionaryEntry[];
  loading?: boolean;
  onAddToWordbook?: (content: string, source: string) => void;
  searchWord?: string;
  relatedWords?: RelatedWord[];
  fuzzyMatches?: FuzzyMatch[];
  onWordClick?: (word: string) => void;
}

const { Option } = Select;

const DictionaryResult: React.FC<DictionaryResultProps> = ({
  entries,
  loading = false,
  onAddToWordbook,
  searchWord,
  relatedWords = [],
  fuzzyMatches = [],
  onWordClick,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<{
    content: string;
    source: string;
  } | null>(null);

  const handleAddToWordbook = (content: string, source: string) => {
    if (isAuthenticated) {
      setSelectedEntry({ content, source });
      setIsModalVisible(true);
    } else {
      // 未登录时的处理
      console.log('请先登录');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedEntry(null);
  };

  const handleCreateSuccess = () => {
    setIsModalVisible(false);
    setSelectedEntry(null);
    // 可以添加成功提示
  };

  // 渲染相关词推荐
  const renderRelatedWords = () => {
    if (!relatedWords || relatedWords.length === 0) return null;

    return (
      <div className={styles.relatedWordsSection}>
        <h3 className={styles.relatedWordsTitle}>相关词推荐</h3>
        <div className={styles.relatedWordsList}>
          {relatedWords.map(word => (
            <div
              key={word.id}
              className={styles.relatedWordItem}
              onClick={() => onWordClick && onWordClick(word.w)}
            >
              <span className={styles.relatedWordText}>{word.w}</span>
              {word.similarity !== undefined && (
                <span className={styles.similarityScore}>{Math.round(word.similarity * 100)}%</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染模糊匹配结果
  const renderFuzzyMatches = () => {
    if (!fuzzyMatches || fuzzyMatches.length === 0) return null;

    const getMatchTypeLabel = (type: string) => {
      switch (type) {
        case 'prefix':
          return '前缀匹配';
        case 'contains':
          return '包含匹配';
        case 'phonetic':
          return '发音匹配';
        case 'tag':
          return '标签匹配';
        default:
          return '匹配';
      }
    };

    return (
      <div className={styles.fuzzyMatchesSection}>
        <h3 className={styles.fuzzyMatchesTitle}>您可能想查找</h3>
        <div className={styles.fuzzyMatchesList}>
          {fuzzyMatches.map(match => (
            <div
              key={match.id}
              className={styles.fuzzyMatchItem}
              onClick={() => onWordClick && onWordClick(match.w)}
            >
              <span className={styles.fuzzyMatchText}>{match.w}</span>
              <span className={styles.matchTypeLabel}>{getMatchTypeLabel(match.matchType)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 创建 Collapse 的 items 属性
  const collapseItems: CollapseProps['items'] = entries.map((entry, index) => ({
    key: index.toString(),
    label: (
      <div className={styles.panelHeader}>
        <div>
          {entry.icon && <span>{entry.icon}</span>}
          <span className={styles.sourceText}>{entry.source}</span>
        </div>
        {entry.sourceType === 'ai' &&
          entry.onRefresh &&
          entry.requested &&
          (entry.refreshCooldown && entry.refreshCooldown > 0 ? (
            <span className={styles.cooldownText} title={`请等待 ${entry.refreshCooldown} 秒`}>
              {entry.refreshCooldown}s 后可再次更新查询结果
            </span>
          ) : (
            <span
              className={styles.textLink}
              onClick={e => {
                e.stopPropagation(); // 防止触发collapse
                entry.onRefresh!();
              }}
            >
              AI解释有误？点击重新查询
              <ReloadOutlined className={styles.refetchIcon} title="刷新AI结果" />
            </span>
          ))}
      </div>
    ),
    className: styles.resultPanel,
    children: entry.isEmpty ? (
      <div className={styles.emptyResult}>
        {entry.loading ? (
          <div className={styles.loading}>加载中...</div>
        ) : entry.requested ? (
          <div className={styles.notFound}>未找到结果</div>
        ) : (
          <div className={styles.requestButton}>
            <button onClick={entry.onRequest}>点击查询</button>
          </div>
        )}
      </div>
    ) : (
      <div className={styles.resultContent}>
        {entry.type === 'html' ? (
          <div dangerouslySetInnerHTML={{ __html: entry.content }} className={styles.htmlContent} />
        ) : (
          <div className={styles.textContent}>{entry.content}</div>
        )}
        {isAuthenticated && !isLoading && (
          <div className={styles.actionButtons}>
            <button
              className={styles.addToWordbookButton}
              onClick={() => handleAddToWordbook(entry.content, entry.source)}
            >
              <PlusOutlined /> 添加到单词本
            </button>
          </div>
        )}
      </div>
    ),
  }));

  return (
    <div className={styles.resultContainer}>
      <Collapse defaultActiveKey={['0']} className={styles.resultCollapse} items={collapseItems} />

      {/* Temporarily comment out the modal component until it's created */}
      {/* {selectedEntry && (
        <CreateWordModal
          visible={isModalVisible}
          onCancel={handleModalCancel}
          initialContent={selectedEntry.content}
          source={selectedEntry.source}
          searchWord={searchWord}
          onSuccess={handleCreateSuccess}
        />
      )} */}

      {renderRelatedWords()}
      {renderFuzzyMatches()}
    </div>
  );
};

export default DictionaryResult;
